// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Alert } from '@mui/material';
import { DenyList } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Rbac/Deny';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import { useRequestFreeze } from '../../../hooks/mutations/registry/freeze/useRequestFreeze';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import useRegistrarServices from '../../../hooks/queries/registry/onboarding/useRegistrarServices';
import useHoldings from '../../../hooks/queries/registry/useHoldings';
import { useInstrumentConfigurationsById } from '../../../hooks/queries/useGetAllInstrumentConfigurations';
import TemplateContract from '../../../utils/TemplateContract';
import { Denial } from './types';

type DialogProps = {
  open: boolean;
  denyLists: TemplateContract<DenyList>[];
  denials: Denial[];
  onClose: () => void;
};

export const RequestFreezeDialog = ({ open, onClose, denyLists, denials }: DialogProps) => {
  const requestFreeze = useRequestFreeze();
  const registrarServices = useRegistrarServices();

  const credentials = useCredentials();
  const holdings = useHoldings();

  const [holder, setHolder] = useState<string>('');
  const [tokenId, setTokenId] = useState<string>('');
  const [reason, setReason] = useState<string>('');

  const instruments = useInstrumentConfigurationsById();

  const resetState = useCallback(() => {
    setHolder('');
    setTokenId('');
    setReason('');
  }, [setHolder, setTokenId, setReason]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const freezeRequest = async () => {
    const instrument = instruments[tokenId];

    if (!instrument) {
      // This should never happen, since the button is disabled if the instrument
      // cannot be found among the known configurations.
      throw Error(`Cannot find instrument configuration for token: ${tokenId}`);
    }

    const holderCredentials = (credentials.data || []).find((c) => c.payload.holder === holder);
    const registrarService = registrarServices.data && registrarServices.data[0];

    if (!holderCredentials) {
      throw Error(`Cannot find credential contract for holder: ${holder}`);
    }
    if (!registrarService) {
      throw Error(`Cannot find registrar service`);
    }

    await requestFreeze({
      registrarServiceCid: registrarService.contractId,
      credentialCids: [holderCredentials.contractId],
      denyListCids: denyLists.map((c) => c.contractId),
      holdingCids: (holdings.data || [])
        .filter((h) => h.payload.owner === holder && h.payload.instrument.id === tokenId)
        .map((h) => h.contractId),
      freezeId: uuidv4(),
      reason,
      instrumentConfigurationCid: instrument.contract.contractId,
    });
    handleClose();
  };

  const holderTokenMatch = useMemo(
    () => denials.find((c) => c.holder === holder && c.instrumentId === tokenId),
    [denials, holder, tokenId],
  );

  const disableSubmit = !tokenId || !instruments[tokenId] || !!holderTokenMatch;

  return (
    <FormDialog
      open={open}
      title="Request Freeze"
      onCancel={handleClose}
      onSubmit={freezeRequest}
      submitName="Request"
      isSubmitDisabled={disableSubmit}
    >
      <TextInput
        enableTopLabel
        label="Instrument ID"
        value={tokenId}
        setValue={setTokenId}
        required
      />
      <TextInput enableTopLabel label="Holder" value={holder} setValue={setHolder} required />
      <TextInput enableTopLabel label="Reason" value={reason} setValue={setReason} />
      {holderTokenMatch && (
        <Alert severity="warning">
          This holder is already frozen for the specified instrument.
        </Alert>
      )}
    </FormDialog>
  );
};
